import React, { useContext, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type ProfileFormState = {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
};

const Settings = () => {
  const { state, login } = useContext(GlobalContext);
  const token = state?.auth?.token || null;
  const currentRoles = Array.isArray(state?.auth?.user?.roles) ? state.auth.user.roles : [];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState<ProfileFormState>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
  });

  let roleLabel = 'User';
  if (currentRoles.includes('super_admin')) {
    roleLabel = 'Super Admin';
  } else if (currentRoles.includes('store_admin')) {
    roleLabel = 'Store Admin';
  }

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) return;
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = response?.data || {};
        setForm((prev) => ({
          ...prev,
          username: user.username || '',
          email: user.email || '',
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          password: '',
        }));
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [token]);

  const onChange = (key: keyof ProfileFormState, value: string) => {
    setSuccess('');
    setError('');
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload: Record<string, string> = {
        username: form.username.trim(),
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      };

      if (form.password.trim()) {
        payload.password = form.password.trim();
      }

      const response = await axios.put('/users/me', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const nextUser = response?.data || {};
      if (token) {
        login(token, nextUser);
      }
      setForm((prev) => ({ ...prev, password: '' }));
      setSuccess('Profile updated successfully.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Settings</h4>
          <Badge bg="dark">{roleLabel}</Badge>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}
        {success ? <Alert variant="success">{success}</Alert> : null}

        {loading ? (
          <div className="py-4 text-center">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading profile...
          </div>
        ) : (
          <Form onSubmit={handleSave}>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  value={form.username}
                  onChange={(e) => onChange('username', e.target.value)}
                  required
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={form.email}
                  onChange={(e) => onChange('email', e.target.value)}
                  required
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  value={form.firstName}
                  onChange={(e) => onChange('firstName', e.target.value)}
                  required
                />
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  value={form.lastName}
                  onChange={(e) => onChange('lastName', e.target.value)}
                  required
                />
              </Col>
              <Col md={6} className="mb-4">
                <Form.Label>New Password (optional)</Form.Label>
                <Form.Control
                  type="password"
                  value={form.password}
                  onChange={(e) => onChange('password', e.target.value)}
                  placeholder="Leave blank to keep current password"
                />
              </Col>
            </Row>

            <Button type="submit" variant="dark" disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

export default Settings;
